<script setup lang="ts">
import CornerLogo from '@/components/CornerLogo.vue';
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import ImageCarousel from '@/components/ImageCarousel.vue';
import Price from '@/components/Price.vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { watchOverlayBool } from '@/composables/overlays.ts';
import { useSidebar } from '@/composables/sidebar.ts';
import {
  DEFAULT_OPTION_TITLE,
  DEFAULT_OPTION_VALUE_TITLE,
  type OptionValue,
  type Product,
  type VariantByOptions,
  getVariantByOptions,
  decodeVariantAvailability,
  isVariantAvailable,
} from '@/lib/shopify/product.ts';
import { sendPlausible } from '@/lib/utils';
import { useShopify } from '@/stores/shopify.ts';
import { VisuallyHidden } from 'radix-vue';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  product: Product;
}>();

const shopify = useShopify();

type SelectedOption = {
  optionIndex: number;
  valueIndex: number;
  name: string;
};

const selectedOptions = ref<Map<string, SelectedOption>>(new Map());
const calculatedVariant = ref<VariantByOptions | null>(null);

const loadingPrice = ref(false);

watch(
  selectedOptions,
  async () => {
    loadingPrice.value = true;

    if (props.product.options.length === selectedOptions.value.size) {
      const options = Array.from(selectedOptions.value.entries()).map(([name, { name: value }]) => ({ name, value }));

      calculatedVariant.value = await getVariantByOptions(props.product.id, options);
    }

    loadingPrice.value = false;
  },
  { deep: true },
);

const variantAvailability = ref<number[][]>([]);
const variantExistence = ref<number[][]>([]);

function onProductChange() {
  selectedOptions.value = new Map();

  variantAvailability.value = decodeVariantAvailability(props.product.encodedVariantAvailability ?? '');
  variantExistence.value = decodeVariantAvailability(props.product.encodedVariantExistence ?? '');

  if (props.product.options.length > 0) {
    for (let optionIndex = 0; optionIndex < props.product.options.length; optionIndex++) {
      const { name: optionName, optionValues } = props.product.options[optionIndex];
      const { name: valueName } = optionValues[0] || { name: null };

      if (valueName === null) {
        console.warn('No option values found:', props.product);
        continue;
      }

      selectedOptions.value.set(optionName, { optionIndex, valueIndex: 0, name: valueName });
    }
  } else {
    console.error('No options found:', props.product);
  }
}

watch(() => props.product, onProductChange, { immediate: true });

const addingToCart = ref(false);

const sidebarOpen = useSidebar();

async function addToCart() {
  addingToCart.value = true;

  if (!calculatedVariant.value) {
    console.error('No variant found:', props.product, selectedOptions.value);
    return;
  }

  shopify.addToCart(calculatedVariant.value.id, 1);

  addingToCart.value = false;

  sidebarOpen.value = true;

  sendPlausible('Added to cart', {
    props: {
      productId: props.product.id,
      product: props.product.title,
      variantId: calculatedVariant.value.id,
      variant: calculatedVariant.value.title,
    },
  });

  // todo: toast maybe

  // toast('Added to cart', {
  //   description: `Added ${product.value?.title} - ${variant?.title} to cart${
  //     quantity.value > 1 ? ` (x${quantity.value})` : ''
  //   }`,
  // });
}

const isSale = computed(() => {
  const compareAt = calculatedVariant.value?.compareAtPrice?.amount ?? null;
  const base = calculatedVariant.value?.price.amount ?? null;

  if (compareAt === null || base === null) {
    return false;
  }

  return Number.parseFloat(base) < Number.parseFloat(compareAt);
});

const hasOptions = computed(() => {
  if (props.product.options.length === 0) {
    return false;
  }

  const firstOption = props.product.options[0];

  return firstOption.name !== DEFAULT_OPTION_TITLE && firstOption.optionValues[0].name !== DEFAULT_OPTION_VALUE_TITLE;
});

const dialogImageIndex = ref(0);
const dialogOpen = ref(false);

function imageClicked(index: number) {
  dialogOpen.value = true;
  dialogImageIndex.value = index;
}

watchOverlayBool(dialogOpen);

function selectedVariantAvailable(selectedOptions: Map<string, SelectedOption>) {
  const combined = [];

  for (const selected of selectedOptions.values()) {
    combined[selected.optionIndex] = selected.valueIndex;
  }

  const available = isVariantAvailable(variantAvailability.value, combined);

  // console.info(
  //   'Options available:',
  //   combined.map((v, i) => `${props.product.options[i].name}: ${props.product.options[i].optionValues[v].name}`),
  //   available,
  // );

  return available;
}

function optionExists(selectedOptions: Map<string, SelectedOption>, optionIndex: number, valueIndex: number) {
  const combined = [];

  for (const selected of selectedOptions.values()) {
    combined[selected.optionIndex] = selected.valueIndex;
  }

  combined[optionIndex] = valueIndex;

  const exists = isVariantAvailable(variantExistence.value, combined);

  // console.info(
  //   'Options exist:',
  //   combined.map((v, i) => `${props.product.options[i].name}: ${props.product.options[i].optionValues[v].name}`),
  //   exists,
  // );

  return exists;
}

const outOfStock = computed(() => !selectedVariantAvailable(selectedOptions.value));
</script>

<template>
  <main>
    <Header />

    <section
      v-if="product"
      class="relative bg-primary rounded-lg p-4 md:pt-24 flex justify-center col-span-12 md:min-h-[calc(100vh-3rem)]"
    >
      <CornerLogo class="hidden md:block" />

      <div class="flex flex-col md:grid md:grid-cols-12 gap-4 max-w-7xl">
        <div class="col-span-7 md:mt-24">
          <ImageCarousel :images="product.images.nodes" @click="imageClicked" />
        </div>

        <Dialog :open="dialogOpen" @update:open="(v) => (dialogOpen = v)">
          <VisuallyHidden>
            <DialogTitle>Product Image</DialogTitle>
            <DialogDescription>View a larger version of the product image.</DialogDescription>
          </VisuallyHidden>
          <DialogContent class="carousel-dialog">
            <ImageCarousel :images="product.images.nodes" v-model="dialogImageIndex" />
          </DialogContent>
        </Dialog>

        <div class="flex flex-col space-y-4 col-span-5">
          <h1 class="text-4xl font-semibold">{{ product.title }}</h1>

          <span class="text-xl font-bold flex gap-2" :class="{ 'text-neutral-600': loadingPrice }">
            <template v-if="isSale">
              <span class="text-red-500"><Price :value="calculatedVariant?.price" /></span>
              <span class="line-through font-normal">
                <Price :value="calculatedVariant?.compareAtPrice" />
              </span>
            </template>
            <Price v-else :value="calculatedVariant?.price" />
          </span>

          <div v-if="hasOptions" class="flex flex-col gap-2">
            <div v-for="(option, i) in product.options" :key="option.id">
              <Label class="font-semibold">{{ option.name }}</Label>
              <div class="mt-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-2">
                <Button
                  v-for="(value, j) in option.optionValues"
                  :key="value.id"
                  variant="outline"
                  size="lg"
                  class="text-lg flex-[1_0_auto]"
                  :class="{ 'bg-black text-white hover:bg-black': selectedOptions.get(option.name)?.valueIndex === j }"
                  @click="
                    selectedOptions.set(option.name, {
                      optionIndex: i,
                      valueIndex: j,
                      name: value.name,
                    })
                  "
                  :disabled="!optionExists(selectedOptions, i, j)"
                >
                  <span>{{ value.name }}</span>
                </Button>
              </div>
            </div>
          </div>

          <Button :disabled="addingToCart || outOfStock" class="text-xl" variant="accent" size="xl" @click="addToCart">
            <span v-if="!outOfStock">Order Now</span>
            <span v-else>Out of Stock</span>
          </Button>

          <slot name="product-details" :variant="calculatedVariant" />

          <div v-if="!$slots?.default" class="product-desc" v-html="product.descriptionHtml"></div>
        </div>
      </div>
    </section>

    <slot />

    <Footer />
  </main>
</template>

<style>
.product-desc {
  @apply flex flex-col gap-2 font-lexend;

  h1 {
    @apply text-2xl font-bold mt-4;
  }

  h2 {
    @apply text-xl font-bold mt-4;
  }
}

.carousel-dialog {
  @apply w-full h-full;

  > .carousel {
    @apply w-full;

    .carousel-content-inner {
      @apply h-full;
    }

    img {
      @apply w-full object-contain bg-black;
    }
  }
}

.carousel-dialog,
.carousel-dialog > * {
  @apply max-w-[calc(100vw-3rem)] max-h-[calc(100svh-3rem)] md:max-w-[calc(100vw-6rem)] md:max-h-[calc(100svh-6rem)];
}
</style>
